import React, { useMemo, useRef, useState } from 'react';
import { TreeNode, TreeViewProps } from '../../../types/DesignSystem/TreeView';
import { ChevronRight } from '../Iconography/Icons';
import Spinner from '../Spinner/Spinner';
import { FOCUS_RING_INSET } from '../focusRing';
import { withClassName } from '../cn';
import { handleListNavKey } from '../listKeyboardNav';
import { useControllableState } from '../useControllableState';
import { useMergedRef } from '../useMergedRef';

/** The fallback key list: one reference, so an empty controlled tree is not a new array per render. */
const NONE: string[] = [];
/* A top-level chevron starts 4px in; each level adds the chevron (16) and the gap (4), so a
   child's chevron sits exactly under its parent's label. */
const INDENT_BASE = 4;
const INDENT_STEP = 20;
const ITEM = '[role="treeitem"]';

interface Row {
  node: TreeNode;
  level: number;
  posinset: number;
  setsize: number;
  parentId: string | null;
  parent: boolean;
  open: boolean;
}

/** The nodes a user can see, in reading order: every root, and the children of every open parent. */
const visibleRows = (nodes: TreeNode[], expanded: Set<string>): Row[] => {
  const rows: Row[] = [];
  const walk = (list: TreeNode[], level: number, parentId: string | null) => {
    list.forEach((node, index) => {
      const parent = Boolean(node.hasChildren) || (node.children?.length ?? 0) > 0;
      const open = parent && expanded.has(node.id);
      rows.push({ node, level, posinset: index + 1, setsize: list.length, parentId, parent, open });
      if (open && node.children) walk(node.children, level + 1, node.id);
    });
  };
  walk(nodes, 1, null);
  return rows;
};

/**
 * TreeView (§ 373) — see the type file for why it exists.
 *
 * The keyboard is the APG tree view's, one-for-one:
 * - Up / Down: the previous / next visible node; Home / End: the first / last. No wrap.
 * - Right: opens a closed parent; on an open one, moves to its first child.
 * - Left: closes an open parent; otherwise moves to the parent.
 * - Enter / Space / click: selects. Focus moving never selects, so walking the tree does not
 *   load every node you pass.
 * - `*`: opens every parent beside the focused node.
 * - Type-ahead: the next node whose label starts with what is typed (§ 364's one search).
 * Up / Down / Home / End and type-ahead are `listKeyboardNav`'s, with `skipDisabled: false`:
 * a node that cannot be selected must still be reachable, or its branch could never open.
 *
 * One tab stop (roving tabindex): the node last focused while it is visible, else the
 * selected node, else the first. Clicking the chevron opens or closes; clicking the row
 * selects. Selection is a tint, and under forced colors the system Highlight, since forced
 * colors drop author fills (§ 224).
 */
const TreeView = React.forwardRef<HTMLDivElement, TreeViewProps>(function TreeView({
  nodes,
  expandedKeys,
  defaultExpandedKeys,
  onExpandedChange,
  selectedKey,
  defaultSelectedKey,
  onSelectionChange,
  loadingKeys = NONE,
  loadingLabel = 'Loading…',
  className = '',
  'data-testid': dataTestId,
  onKeyDown,
  ...rest
}, forwardedRef) {
  const treeRef = useRef<HTMLDivElement>(null);
  const ref = useMergedRef(forwardedRef, treeRef);
  const [expanded, setExpanded] = useControllableState<string[]>(expandedKeys, defaultExpandedKeys, NONE);
  const [selected, setSelected] = useControllableState<string | null>(selectedKey, defaultSelectedKey, null);
  const [active, setActive] = useState<string | null>(null);
  const expandedSet = useMemo(() => new Set(expanded), [expanded]);
  const rows = useMemo(() => visibleRows(nodes, expandedSet), [nodes, expandedSet]);
  const byKey = new Map(rows.map((row) => [row.node.id, row]));
  const loading = new Set(loadingKeys);

  const tabStop = (active !== null && byKey.has(active) ? active : null)
    ?? (selected != null && byKey.has(selected) ? selected : null)
    ?? rows[0]?.node.id
    ?? null;

  const setOpen = (keys: string[], open: boolean) => {
    const next = open
      ? [...expanded, ...keys.filter((key) => !expandedSet.has(key))]
      : expanded.filter((key) => !keys.includes(key));
    if (next.length === expanded.length) return;
    setExpanded(next);
    onExpandedChange?.(next);
  };
  const select = (row: Row) => {
    if (row.node.disabled) return;
    setSelected(row.node.id);
    onSelectionChange?.(row.node.id);
  };
  const focusKey = (key: string) => {
    const items = treeRef.current?.querySelectorAll<HTMLElement>(ITEM) ?? [];
    [...items].find((item) => item.getAttribute('data-key') === key)?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    const item = (event.target as HTMLElement).closest<HTMLElement>(ITEM);
    const row = item ? byKey.get(item.getAttribute('data-key') ?? '') : undefined;
    if (!row) return;
    switch (event.key) {
      case 'ArrowRight': {
        event.preventDefault();
        if (row.parent && !row.open) setOpen([row.node.id], true);
        else if (row.open) {
          const child = rows[rows.indexOf(row) + 1];
          if (child?.parentId === row.node.id) focusKey(child.node.id);
        }
        return;
      }
      case 'ArrowLeft':
        event.preventDefault();
        if (row.open) setOpen([row.node.id], false);
        else if (row.parentId !== null) focusKey(row.parentId);
        return;
      case 'Enter':
        event.preventDefault();
        select(row);
        return;
      case '*':
        event.preventDefault();
        setOpen(rows.filter((r) => r.parentId === row.parentId && r.parent).map((r) => r.node.id), true);
        return;
      default:
        break;
    }
    if (handleListNavKey(event, { container: treeRef.current, itemSelector: ITEM, loop: false, typeAhead: true, skipDisabled: false })) return;
    /* Space selects, unless it was part of a type-ahead search (consumed above). */
    if (event.key === ' ') {
      event.preventDefault();
      select(row);
    }
  };

  /* No nodes, no tree. A tree must own treeitems (an empty one is invalid ARIA, and an axe
     violation), and callers pass `[]` while the first fetch runs: show a Skeleton or an
     EmptyState there instead. Dropping only the role would leave an aria-label on a plain div,
     which ARIA prohibits too. */
  if (rows.length === 0) return null;

  return (
    <div
      /* rest first — the Button.tsx rule; the role and the keyboard below are the component's. */
      {...rest}
      ref={ref}
      role="tree"
      data-testid={dataTestId}
      className={withClassName('tw-flex tw-flex-col', className)}
      onKeyDown={handleKeyDown}
    >
      {rows.map((row) => {
        const { node } = row;
        const isSelected = selected === node.id;
        const isLoading = loading.has(node.id);
        const testid = node['data-testid'] ?? (dataTestId ? `${dataTestId}-node-${node.id}` : undefined);
        /* Forced colors drop author fills and paint a Canvas backplate behind text, so the tint
           cannot carry selection there and HighlightText on a Highlight row reads white on
           white (measured). Selection is the rail below instead; a node that cannot be
           selected takes GrayText, the system's disabled ink. */
        const tone = node.disabled
          ? 'tw-text-ink-disabled tw-cursor-default forced-colors:tw-text-[color:GrayText]'
          : isSelected
            ? 'tw-bg-accent-tint tw-text-accent tw-cursor-pointer'
            : 'tw-text-ink tw-cursor-pointer hover:tw-bg-surface-hover';
        return (
          <div
            key={node.id}
            role="treeitem"
            aria-level={row.level}
            aria-posinset={row.posinset}
            aria-setsize={row.setsize}
            aria-expanded={row.parent ? row.open : undefined}
            aria-selected={isSelected}
            aria-disabled={node.disabled || undefined}
            aria-busy={isLoading || undefined}
            tabIndex={node.id === tabStop ? 0 : -1}
            data-key={node.id}
            data-testid={testid}
            onFocus={() => setActive(node.id)}
            onClick={() => select(row)}
            style={{ paddingLeft: INDENT_BASE + (row.level - 1) * INDENT_STEP }}
            className={`tw-relative tw-flex tw-items-center tw-gap-1 tw-min-h-8 tw-pr-2 tw-rounded-ctl tw-text-body-primary tw-select-none ${tone} ${FOCUS_RING_INSET}`}
          >
            {isSelected && (
              /* The selection mark under forced colors, the Tab indicator's technique (§ 224): a
                 fill that IS the information takes Highlight, the one background that mode keeps.
                 Transparent otherwise; the tint above is the mark. */
              <span
                aria-hidden="true"
                data-testid={testid ? `${testid}-selected-mark` : undefined}
                className="tw-absolute tw-left-0 tw-top-1 tw-bottom-1 tw-w-1 tw-rounded-full forced-colors:tw-bg-[color:Highlight]"
              />
            )}
            <span
              /* The chevron is decoration (aria-expanded is the state), so the slot is hidden;
                 while loading it holds the spinner, whose role="status" must be heard. */
              aria-hidden={isLoading ? undefined : true}
              className="tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-w-4 tw-h-4"
              /* A mouse shortcut only: the row is the control, and the keyboard opens it with Right. */
              onClick={row.parent ? (event) => { event.stopPropagation(); setOpen([node.id], !row.open); } : undefined}
            >
              {isLoading ? (
                <Spinner size="sm" label={loadingLabel} data-testid={testid ? `${testid}-loading` : undefined} />
              ) : row.parent && (
                <ChevronRight
                  size={16}
                  className={`tw-text-ink-secondary motion-safe:tw-transition-transform motion-safe:tw-duration-base motion-safe:tw-ease-swift ${row.open ? 'tw-rotate-90' : ''}`}
                />
              )}
            </span>
            {node.icon && <span aria-hidden="true" className="tw-flex tw-shrink-0 tw-text-ink-secondary">{node.icon}</span>}
            <span className="tw-min-w-0 tw-truncate" title={node.label}>{node.label}</span>
          </div>
        );
      })}
    </div>
  );
});

export default TreeView;
